/**
 * Sticky "Your Sox Box" progress bar — a row of arrow/chevron segments that fill in as the guided
 * flow is answered.
 *
 * Lives OUTSIDE <product-form> so a no-reload product swap never touches it. It listens on
 * `document` for <guided-selector>'s `on:guided:change` events (which bubble) and rebuilds the
 * segments, price and CTA from event.detail:
 *
 *   { segments: [{ key, label, value }], currentStep, totalSteps, complete,
 *     variant, variantId, available, sellingPlan, price, compareAt }
 *
 * The CTA forwards to the real add-to-cart button in the (possibly just-swapped) product form,
 * which guided-selector.js enables only once every step is answered. The button is re-queried on
 * every click because a swap replaces the form element it belongs to.
 */
if (!customElements.get('guided-sox-box-bar')) {
  // Short, bar-friendly labels per step key (mockup: Height › Color › Size › Quantity › Frequency).
  const LABELS = { height: 'Height', pack: 'Quantity', frequency: 'Frequency' };

  class GuidedSoxBoxBar extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.segmentsEl = this.querySelector('[data-sox-box-segments]');
      this.priceEl = this.querySelector('[data-sox-box-price]');
      this.cta = this.querySelector('[data-sox-box-cta]');
      this.defaultPrice = this.priceEl ? this.priceEl.textContent : '';
      this.defaultCtaText = this.cta ? this.cta.textContent.trim() : '';
      this.soldOutText = this.getAttribute('data-soldout') || 'Sold out';

      // Scope to this bar's own section so multiple product sections never cross-wire.
      this.section = this.closest('.shopify-section') || document;

      this.onGuidedChange = this.onGuidedChange.bind(this);
      document.addEventListener('on:guided:change', this.onGuidedChange);

      if (this.cta) this.cta.addEventListener('click', this.handleCta.bind(this));

      // guided-selector fires its first on:guided:change during its own upgrade — which happens
      // before this element's script runs — so that initial event is missed. Pull the current
      // state now that we're listening, so the bar shows immediately on load.
      const gs = this.section.querySelector && this.section.querySelector('guided-selector');
      if (gs && typeof gs.refresh === 'function') gs.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('on:guided:change', this.onGuidedChange);
    }

    onGuidedChange(evt) {
      const target = evt.target;
      if (this.section !== document && target && !this.section.contains(target)) return;
      this.render(evt.detail || {});
    }

    labelFor(seg) {
      if (LABELS[seg.key]) return LABELS[seg.key];
      const k = String(seg.key || '').toLowerCase();
      if (k.indexOf('colour') > -1 || k.indexOf('color') > -1) return 'Color';
      if (k.indexOf('size') > -1) return 'Size';
      // Fall back to a tidy version of the option name.
      return String(seg.key || '').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    render(detail) {
      const segments = detail.segments || [];
      const activeIndex = detail.complete ? -1 : (detail.currentStep || 0);

      // Reveal the bar once the flow is present.
      this.toggleAttribute('hidden', segments.length === 0);
      this.classList.toggle('is-complete', !!detail.complete);

      if (this.segmentsEl) {
        this.segmentsEl.innerHTML = segments.map((seg, i) => {
          // "done" must mean "reached AND answered, in order" — not just "has a value". Frequency
          // always has a value (a delivery plan is pre-checked by default), so gating on seg.value
          // alone made it render blue from page load, before Height was even touched. currentStep is
          // the count of segments answered in sequence from the start (see guided-selector.js's
          // refresh()/emitChange()), so i < currentStep is exactly "this segment's turn already came
          // and passed" — true for every segment once complete, since currentStep becomes the total.
          const state = i < detail.currentStep ? 'done' : (i === activeIndex ? 'active' : 'future');
          const quiz = this.classList.contains('sox-box-bar--quiz');
          const label = this.escape(this.labelFor(seg));
          return `
            <${quiz ? 'button' : 'div'} class="sox-seg" data-state="${state}" ${quiz ? `type="button" data-quiz-step="${i}" aria-label="Go to ${label}" ${state === 'future' ? 'disabled' : ''}` : 'role="listitem"'}>
              <span class="sox-seg__label">${label}:</span>
              <span class="sox-seg__value">${this.escape(seg.value || '')}</span>
            </${quiz ? 'button' : 'div'}>`;
        }).join('');
      }
      // On mobile, keep the current step visible as the user moves through the flow.
if (this.segmentsEl && window.matchMedia('(max-width: 720px)').matches) {
  requestAnimationFrame(() => {
    const activeSegment = this.segmentsEl.querySelector('[data-state="active"]');

    if (activeSegment) {
      activeSegment.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    } else if (detail.complete) {
      this.segmentsEl.scrollTo({
        left: this.segmentsEl.scrollWidth,
        behavior: 'smooth'
      });
    }
  });
}

      // Price (Liquid-formatted string handed over in the event — never format currency in JS).
      if (this.priceEl) {
        if (detail.price) {
          this.priceEl.innerHTML = detail.compareAt
            ? `<span class="sox-box-bar__price-compare">${detail.compareAt}</span> ${detail.price}`
            : detail.price;
        } else {
          this.priceEl.textContent = this.defaultPrice;
        }
      }

      // CTA is live only when every step is answered and the variant is purchasable. When the chosen
      // combination is complete but out of stock, say so instead of greying out silently.
      if (this.cta) {
        const soldOut = !!(detail.complete && !detail.available);
        const ready = !!(detail.complete && detail.available);
        this.cta.disabled = !ready;
        this.cta.setAttribute('aria-disabled', String(!ready));
        this.cta.classList.toggle('is-soldout', soldOut);
        this.cta.textContent = soldOut ? this.soldOutText : this.defaultCtaText;
      }
    }

    /** Forward to the real add-to-cart button in the current product form.
     * Singular querySelector is intentional and safe here, not a bug: the section can contain TWO
     * .js-product-form elements (the main form and, if enabled, the sticky ATC bar's own form — see
     * snippets/sticky-atc.liquid), but the main <product-form> always precedes <sticky-atc> in
     * document order, so this deterministically resolves to the main form's button, which
     * guided-selector.js already gates correctly. */
    handleCta(evt) {
      if (this.cta.disabled) return;
      evt.preventDefault();
      const addBtn = this.section.querySelector('.js-product-form [name="add"]');
      if (addBtn && !addBtn.disabled) addBtn.click();
    }

    escape(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    }
  }

  customElements.define('guided-sox-box-bar', GuidedSoxBoxBar);
}
